import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { workflowRoleOptions } from 'src/entity/ProjectSettingsForm/ui/workflowSettings';
import { richTextToPlainText } from 'src/shared/lib/richText';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import {
  DirectoryWorkflowHistoryItem,
  GetDirectoryValueWorkflowRes,
} from 'src/shared/api/projects/types';
import { setError, setSuccess } from 'src/shared/lib/utils';
import {
  DirectoryWorkflowSettings,
  FormWorkflowStatus,
  TableSettingsForm,
} from 'src/shared/store/projects/types';
import { Button, RichTextEditor, RichTextViewer } from 'src/shared/ui';

import {
  buildLinearWorkflowRuntimeTransitions,
  formatWorkflowDate,
  getWorkflowStatusLabel,
  HISTORY_PAGE_SIZE,
  MAX_WORKFLOW_COMMENT_LENGTH,
  normalizeWorkflowRuntimeRole,
  normalizeWorkflowRuntimeStatus,
} from 'src/feature/Workflow/lib/runtime';

interface DirectoryWorkflowPanelProps {
  tableSettings: TableSettingsForm;
  selectedValueId: number | null;
  refreshToken: number;
  canTransition: boolean;
  userRole?: string;
}

export const DirectoryWorkflowPanel: FC<DirectoryWorkflowPanelProps> = ({
  tableSettings,
  selectedValueId,
  refreshToken,
  canTransition,
  userRole,
}) => {
  const [workflowState, setWorkflowState] = useState<GetDirectoryValueWorkflowRes | null>(null);
  const [historyItems, setHistoryItems] = useState<DirectoryWorkflowHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentHtml, setCommentHtml] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [forceTransition, setForceTransition] = useState(false);

  const workflowSettings = tableSettings.directoryWorkflowSettings as
    | DirectoryWorkflowSettings
    | undefined;
  const workflowEnabled = Boolean(workflowSettings?.enabled);
  const normalizedRole = useMemo(() => normalizeWorkflowRuntimeRole(userRole), [userRole]);
  const canForceTransition = normalizedRole === 'admin' || normalizedRole === 'form_designer';
  const roleLabelMap = useMemo(
    () =>
      workflowRoleOptions.reduce<Record<string, string>>((acc, role) => {
        acc[role.value] = role.label;
        return acc;
      }, {}),
    [],
  );

  const statuses = useMemo(
    () =>
      [...(workflowSettings?.statuses || [])].sort(
        (a, b) => Number(a.order || 0) - Number(b.order || 0),
      ),
    [workflowSettings?.statuses],
  );

  const transitions = useMemo(() => {
    const configured = workflowSettings?.transitions || [];
    if (configured.length) {
      return configured;
    }
    return buildLinearWorkflowRuntimeTransitions(statuses);
  }, [statuses, workflowSettings?.transitions]);

  const statusMap = useMemo(
    () =>
      statuses.reduce<Record<string, FormWorkflowStatus>>((acc, status) => {
        const code = normalizeWorkflowRuntimeStatus(status.code);
        if (code) {
          acc[code] = { ...status, code };
        }
        return acc;
      }, {}),
    [statuses],
  );

  const currentStatusCode = useMemo(
    () =>
      normalizeWorkflowRuntimeStatus(
        workflowState?.currentStatus || workflowSettings?.initialStatus || '',
      ),
    [workflowState?.currentStatus, workflowSettings?.initialStatus],
  );

  const currentResponsibleRoles = useMemo(
    () =>
      (statusMap[currentStatusCode]?.responsibleRoles || []).map(
        role => roleLabelMap[normalizeWorkflowRuntimeRole(role)] || role,
      ),
    [currentStatusCode, roleLabelMap, statusMap],
  );
  const currentReadRoles = useMemo(
    () =>
      (statusMap[currentStatusCode]?.readRoles || []).map(
        role => roleLabelMap[normalizeWorkflowRuntimeRole(role)] || role,
      ),
    [currentStatusCode, roleLabelMap, statusMap],
  );

  const manualAvailableStatuses = useMemo(() => {
    if (!workflowEnabled || !workflowSettings?.manualTransitionEnabled) {
      return [];
    }

    const result = transitions
      .filter(transition => normalizeWorkflowRuntimeStatus(transition.from) === currentStatusCode)
      .filter(transition => {
        if (!transition.allowedRoles?.length) {
          return true;
        }
        return transition.allowedRoles.some(
          role => normalizeWorkflowRuntimeRole(role) === normalizedRole,
        );
      })
      .map(transition => normalizeWorkflowRuntimeStatus(transition.to))
      .filter(Boolean);

    return result.filter((status, index) => result.indexOf(status) === index);
  }, [
    currentStatusCode,
    normalizedRole,
    transitions,
    workflowEnabled,
    workflowSettings?.manualTransitionEnabled,
  ]);

  const forceAvailableStatuses = useMemo(
    () =>
      statuses
        .map(status => normalizeWorkflowRuntimeStatus(status.code))
        .filter(status => Boolean(status) && status !== currentStatusCode),
    [currentStatusCode, statuses],
  );

  const availableStatuses =
    forceTransition && canForceTransition ? forceAvailableStatuses : manualAvailableStatuses;

  const commentPlainText = useMemo(() => richTextToPlainText(commentHtml), [commentHtml]);
  const isCommentRequired = Boolean(workflowSettings?.requireCommentOnTransition);
  const currentStatusLabel = getWorkflowStatusLabel(currentStatusCode, statusMap);

  const clearEditor = () => {
    setCommentHtml('');
  };

  const loadWorkflowState = useCallback(async () => {
    if (!selectedValueId) {
      setWorkflowState(null);
      return null;
    }

    setWorkflowLoading(true);
    try {
      const { data } = await ProjectsService.getDirectoryValueWorkflow(
        tableSettings.id,
        selectedValueId,
      );
      setWorkflowState(data);
      return data;
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Не удалось загрузить статус справочника');
      return null;
    } finally {
      setWorkflowLoading(false);
    }
  }, [selectedValueId, tableSettings.id]);

  const loadHistory = useCallback(
    async (offset: number, append = false) => {
      if (!selectedValueId) {
        setHistoryItems([]);
        setHistoryTotal(0);
        setHistoryOffset(0);
        return;
      }

      setHistoryLoading(true);
      try {
        const { data } = await ProjectsService.getDirectoryValueWorkflowHistory(
          tableSettings.id,
          selectedValueId,
          {
            limit: HISTORY_PAGE_SIZE,
            offset,
          },
        );
        const items = data?.items || [];
        setHistoryItems(prev => (append ? [...prev, ...items] : items));
        setHistoryTotal(data?.total || 0);
        setHistoryOffset(offset + items.length);
      } catch (error) {
        console.error('Failed to load directory workflow history', error);
        setError('Не удалось загрузить историю бизнес-процесса справочника');
      } finally {
        setHistoryLoading(false);
      }
    },
    [selectedValueId, tableSettings.id],
  );

  const handleTransition = async () => {
    if (!selectedValueId) {
      setError('Выберите сохранённое значение справочника');
      return;
    }
    if (!selectedStatus) {
      setError('Выберите целевой статус');
      return;
    }
    if (commentHtml.length > MAX_WORKFLOW_COMMENT_LENGTH) {
      setError('Комментарий к переходу превышает максимально допустимую длину');
      return;
    }
    if (isCommentRequired && !commentPlainText) {
      setError('Для перехода требуется комментарий');
      return;
    }

    setIsSubmitting(true);
    try {
      await ProjectsService.transitionDirectoryValueWorkflow(tableSettings.id, selectedValueId, {
        toStatus: selectedStatus,
        comment: commentHtml,
        force: forceTransition && canForceTransition,
      });
      await loadWorkflowState();
      await loadHistory(0, false);
      clearEditor();
      setForceTransition(false);
      setSuccess(
        `Статус значения #${selectedValueId} изменён на ${getWorkflowStatusLabel(
          selectedStatus,
          statusMap,
        )}`,
      );
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Не удалось выполнить переход');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!workflowEnabled || !selectedValueId) {
      setWorkflowState(null);
      setHistoryItems([]);
      setHistoryTotal(0);
      setHistoryOffset(0);
      return;
    }

    void loadWorkflowState();
    void loadHistory(0, false);
  }, [loadHistory, loadWorkflowState, refreshToken, selectedValueId, workflowEnabled]);

  useEffect(() => {
    if (!availableStatuses.length) {
      setSelectedStatus('');
      return;
    }
    setSelectedStatus(prev =>
      prev && availableStatuses.includes(normalizeWorkflowRuntimeStatus(prev))
        ? prev
        : availableStatuses[0],
    );
  }, [availableStatuses]);

  if (!workflowEnabled) {
    return null;
  }

  if (!selectedValueId) {
    return (
      <div className="mb-4 rounded border border-blue-400 bg-white p-4 shadow-white-xl dark:bg-violet-1100">
        <div className="font-gothampro-500 text-base dark:text-white">
          Бизнес-процесс справочника
        </div>
        <div className="font-gothampro-400 mt-2 text-sm opacity-70 dark:text-white">
          Выберите сохранённую строку справочника, чтобы управлять её статусом.
        </div>
      </div>
    );
  }

  const noMoreHistory = historyOffset >= historyTotal;
  const transitionBlocked =
    !canTransition ||
    !availableStatuses.length ||
    (!forceTransition && !workflowSettings?.manualTransitionEnabled);

  return (
    <div className="mb-4 rounded border border-blue-400 bg-white p-4 shadow-white-xl dark:bg-violet-1100">
      <div className="mb-3 flex items-center">
        <div className="font-gothampro-500 text-base dark:text-white">
          {`Бизнес-процесс справочника: значение #${selectedValueId}`}
        </div>
        <Button
          size="small"
          classNames="ml-auto w-28"
          variant="greyLight"
          onClick={() => {
            void loadWorkflowState();
            void loadHistory(0, false);
          }}
          disabled={historyLoading || workflowLoading || isSubmitting}
        >
          Обновить
        </Button>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="rounded border border-blue-400 p-3">
          <div className="text-extra-xs opacity-70 dark:text-white">Текущий статус</div>
          <div className="font-gothampro-500 mt-1 dark:text-white">{currentStatusLabel}</div>
          {workflowState?.readOnly && (
            <div className="mt-2 text-extra-xs text-red-400">
              Значение доступно только для чтения в текущем статусе
            </div>
          )}
        </div>

        <div className="rounded border border-blue-400 p-3">
          <div className="text-extra-xs opacity-70 dark:text-white">Целевой статус</div>
          <select
            className="font-gothampro-400 mt-2 w-full rounded border border-violet-400 bg-white p-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            value={selectedStatus}
            onChange={event => setSelectedStatus(event.target.value)}
            disabled={transitionBlocked || isSubmitting}
          >
            {!availableStatuses.length && <option value="">Нет доступных переходов</option>}
            {availableStatuses.map(statusCode => (
              <option key={statusCode} value={statusCode}>
                {getWorkflowStatusLabel(statusCode, statusMap)}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded border border-blue-400 p-3">
          <div className="text-extra-xs opacity-70 dark:text-white">Настройки перехода</div>
          <div className="font-gothampro-400 mt-1 text-xs dark:text-white">
            {workflowSettings?.manualTransitionEnabled
              ? 'Ручные переходы включены'
              : 'Ручные переходы отключены, статус меняется автоматически после сохранения'}
          </div>
          {currentResponsibleRoles.length > 0 && (
            <div className="font-gothampro-400 mt-2 text-xs dark:text-white">
              {`Ответственные роли: ${currentResponsibleRoles.join(', ')}`}
            </div>
          )}
          <div className="font-gothampro-400 mt-2 text-xs dark:text-white">
            {currentReadRoles.length > 0
              ? `Чтение: ${currentReadRoles.join(', ')}`
              : 'Чтение: все роли с доступом к форме'}
          </div>
          {isCommentRequired && (
            <div className="font-gothampro-400 mt-2 text-xs text-red-400">
              Комментарий обязателен для каждого перехода
            </div>
          )}
          {canForceTransition && (
            <label className="font-gothampro-400 mt-2 flex items-center gap-2 text-xs dark:text-white">
              <input
                type="checkbox"
                checked={forceTransition}
                onChange={event => setForceTransition(event.target.checked)}
                disabled={isSubmitting || forceAvailableStatuses.length === 0}
              />
              Принудительный переход
            </label>
          )}
        </div>
      </div>

      <div className="mb-3">
        <RichTextEditor
          value={commentHtml}
          onChange={setCommentHtml}
          placeholder="Добавьте комментарий к переходу..."
          disabled={isSubmitting}
          maxLength={MAX_WORKFLOW_COMMENT_LENGTH}
          onError={setError}
        />
        <div className="mt-1 text-right text-extra-xs opacity-60 dark:text-white">
          {commentHtml.length}/{MAX_WORKFLOW_COMMENT_LENGTH}
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Button
          size="small"
          classNames="w-48"
          variant="green"
          onClick={() => void handleTransition()}
          disabled={transitionBlocked || isSubmitting}
        >
          Выполнить переход
        </Button>
        <Button
          size="small"
          classNames="w-32"
          variant="greyLight"
          onClick={clearEditor}
          disabled={isSubmitting}
        >
          Очистить
        </Button>
      </div>

      <div className="max-h-56 overflow-y-auto rounded border border-blue-400 p-2">
        {(historyLoading || workflowLoading) && (
          <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
            Загрузка истории бизнес-процесса...
          </div>
        )}
        {!historyLoading && !workflowLoading && !historyItems.length && (
          <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
            История переходов пока пуста
          </div>
        )}

        {!historyLoading &&
          historyItems.map(item => (
            <div key={item.ID} className="mb-2 rounded border border-blue-400 p-3">
              <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                {formatWorkflowDate(item.ChangedAt)}
              </div>
              <div className="font-gothampro-500 text-sm dark:text-white">
                {getWorkflowStatusLabel(item.FromStatus, statusMap)} -&gt;{' '}
                {getWorkflowStatusLabel(item.ToStatus, statusMap)}
              </div>
              <div className="text-extra-xs opacity-70 dark:text-white">
                {item.ChangedByEmail || `user #${item.ChangedByID}`} ({item.ChangedByRole || '-'})
              </div>
              <div className="font-gothampro-400 mt-2 rounded bg-blue-rgba-100 p-2 text-xs text-black dark:text-white">
                <RichTextViewer
                  value={item.Comment}
                  emptyText="Комментарий отсутствует"
                  className="break-words"
                />
              </div>
            </div>
          ))}
      </div>

      {!historyLoading && historyItems.length > 0 && !noMoreHistory && (
        <div className="mt-2 flex justify-center">
          <Button
            size="small"
            classNames="w-48"
            variant="violetOutline"
            onClick={() => void loadHistory(historyOffset, true)}
            disabled={isSubmitting}
          >
            Загрузить ещё
          </Button>
        </div>
      )}
    </div>
  );
};
